import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@valiant/ui-shared'
import { ChevronsUpDown } from 'lucide-react'
import type { Company, Group } from '../../generated/graphql'
import { useNavigate } from 'react-router-dom'

interface SelectorItem {
  id: string
  name: string
  hasBalance?: boolean
  hasLpTransfer?: boolean
  hasPendingApprovals?: boolean
}

interface DynamicPopoverProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  loading: boolean
  selectedValue: string
  searchValue: string
  onSearchChange: (value: string) => void
  items: SelectorItem[]
  selectedId: string
  onItemSelect: (item: SelectorItem) => void
  placeholder?: string
  emptyMessage?: string
}

function DynamicPopover({
  isOpen,
  onOpenChange,
  loading,
  selectedValue,
  searchValue,
  onSearchChange,
  items,
  selectedId,
  onItemSelect,
  placeholder = 'Search...',
  emptyMessage = 'No items found.',
}: DynamicPopoverProps) {
  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          className="flex-1 min-w-0 h-10 md:h-auto md:w-48 md:flex-none justify-between bg-transparent border border-main md:border-card-border text-main hover:text-main md:hover:text-default md:text-default md:bg-white"
          disabled={loading}
        >
          <span className="text-ellipsis overflow-hidden font-semibold">{selectedValue}</span>
          <ChevronsUpDown className="ml-2 size-6 shrink-0 md:size-4 md:opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] md:w-48 p-0 bg-transparent border border-main md:bg-popover md:border md:border-input md:text-popover-foreground">
        <Command
          shouldFilter={false}
          className="text-command-foreground"
          defaultValue={items.find((item) => item.id === selectedId)?.name || ''}
        >
          <CommandInput
            placeholder={placeholder}
            value={searchValue}
            onValueChange={onSearchChange}
            className="border-main md:text-command-foreground md:border-input"
          />
          <CommandList>
            <CommandEmpty className="md:text-muted-foreground px-4 pt-2">
              {loading ? 'Loading...' : emptyMessage}
            </CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.name}
                  onSelect={() => {
                    onItemSelect(item)
                    onOpenChange(false)
                  }}
                  className="text-ellipsis overflow-hidden text-main hover:bg-main/5 hover:text-main md:text-default md:hover:bg-accent md:hover:text-accent-foreground cursor-pointer"
                >
                  {item.name}
                  {item.hasBalance && (
                    <Badge
                      variant="outline"
                      className="ml-1 h-2 w-2 p-0 rounded-full bg-green-500 border-green-500"
                    >
                      <span className="sr-only">Has balance</span>
                    </Badge>
                  )}
                  {item.hasLpTransfer && (
                    <Badge
                      variant="outline"
                      className="ml-1 h-2 w-2 p-0 rounded-full bg-yellow-500 border-yellow-500"
                    >
                      <span className="sr-only">Has LP transfer</span>
                    </Badge>
                  )}
                  {item.hasPendingApprovals && (
                    <Badge
                      variant="outline"
                      className="ml-1 h-2 w-2 p-0 rounded-full bg-red-500 border-red-500"
                    >
                      <span className="sr-only">Has pending approvals</span>
                    </Badge>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface DynamicSelectorProps {
  redirectOnSelect: boolean
  isSwapMode: boolean
  selectedGroupId: string
  selectedCompanyId: string
  groupsData: Group[]
  companyData: Company[]
  groupSearchValue: string
  setGroupSearchValue: (value: string) => void
  companySearchValue: string
  setCompanySearchValue: (value: string) => void
  groupComboboxOpen: boolean
  setGroupComboboxOpen: (open: boolean) => void
  groupsLoading: boolean
  companyComboboxOpen: boolean
  setCompanyComboboxOpen: (open: boolean) => void
  companiesLoading: boolean
  setSelectedGroupId: (groupId: string) => void
  setSelectedCompanyId: (companyId: string) => void
}

function DynamicSelector({
  redirectOnSelect,
  isSwapMode,
  selectedGroupId,
  selectedCompanyId,
  groupsData,
  companyData,
  groupSearchValue,
  setGroupSearchValue,
  companySearchValue,
  setCompanySearchValue,
  groupComboboxOpen,
  setGroupComboboxOpen,
  groupsLoading,
  companyComboboxOpen,
  setCompanyComboboxOpen,
  companiesLoading,
  setSelectedGroupId,
  setSelectedCompanyId,
}: DynamicSelectorProps) {
  const navigate = useNavigate()

  // Get selected group and company names for display
  const selectedGroup = groupsData?.find((group) => group.groupId === selectedGroupId)
  const selectedCompany = companyData?.find((company) => company.id === selectedCompanyId)

  // Transform and filter data
  const filteredGroups = groupsData
    ?.filter((group) => group.name.toLowerCase().includes(groupSearchValue.toLowerCase()))
    .map((group) => ({
      id: group.groupId,
      name: group.name,
      hasBalance: false,
      hasPendingApprovals: group.pendingNotifications > 0,
    }))

  const filteredCompanies = companyData
    ?.filter((company) => company.company.toLowerCase().includes(companySearchValue.toLowerCase()))
    .map((company) => ({
      id: company.id,
      name: company.company,
      hasBalance: company.hasBalance,
      hasLpTransfer: company.hasLpTransfer,
    }))

  const hasAnyCompanyBalance = filteredCompanies?.some((company) => company.hasBalance) ?? false
  const hasAnyGroupPendingNotifications =
    groupsData?.some((g) => g.pendingNotifications > 0) ?? false

  // Handle item selection with navigation logic
  const handleGroupSelect = (item: SelectorItem) => {
    if (item.id !== selectedGroupId) {
      setSelectedGroupId(item.id)
      if (redirectOnSelect) navigate(`/`, { replace: true })
    }
  }

  const handleCompanySelect = (item: SelectorItem) => {
    if (item.id !== selectedCompanyId) {
      setSelectedCompanyId(item.id)
      if (redirectOnSelect) navigate(`/`, { replace: true })
    }
  }

  return !isSwapMode ? (
    // Group Selector for Admin Mode
    <div className="flex items-center gap-2">
      <DynamicPopover
        isOpen={groupComboboxOpen}
        onOpenChange={setGroupComboboxOpen}
        loading={groupsLoading}
        selectedValue={
          selectedGroup ? selectedGroup.name : groupsLoading ? 'Loading...' : 'Select Group'
        }
        searchValue={groupSearchValue}
        onSearchChange={setGroupSearchValue}
        items={filteredGroups || []}
        selectedId={selectedGroupId}
        onItemSelect={handleGroupSelect}
        placeholder="Search groups..."
        emptyMessage="No groups found."
      />
      {hasAnyGroupPendingNotifications && (
        <img src="/notification.png" alt="Notification" className="size-6 shrink-0 md:size-4" />
      )}
    </div>
  ) : (
    // Company Selector for Swap Mode
    <div className="flex items-center gap-2">
      <DynamicPopover
        isOpen={companyComboboxOpen}
        onOpenChange={setCompanyComboboxOpen}
        loading={companiesLoading || groupsLoading}
        selectedValue={
          selectedCompany
            ? selectedCompany.company
            : companiesLoading || groupsLoading
              ? 'Loading...'
              : 'Select Company'
        }
        searchValue={companySearchValue}
        onSearchChange={setCompanySearchValue}
        items={filteredCompanies || []}
        selectedId={selectedCompanyId}
        onItemSelect={handleCompanySelect}
        placeholder="Search companies..."
        emptyMessage="No companies found."
      />
      {hasAnyCompanyBalance && (
        <img src="/notification.png" alt="Notification" className="size-6 shrink-0 md:size-4" />
      )}
    </div>
  )
}

export default DynamicSelector
